import { useState } from 'react';
import { useSelector } from 'react-redux';
import fm from 'format-message';
import PropTypes from 'prop-types';
import useSiteSelectorQuery from 'queries/useSiteSelectorQuery';
import Image from '@activebrands/core-web/components/Image';
import AnimatedOverlay from '@activebrands/core-web/components/overlays/AnimatedOverlay';
import media from '@activebrands/core-web/config/media';
import { OVERLAY_ID } from '@activebrands/core-web/config/overlay';
import { useDidUpdate } from '@activebrands/core-web/hooks/lifecycle';
import overlay from '@activebrands/core-web/libs/overlay';
import useScrollLock from '@activebrands/core-web/libs/scroll';
import { styled, useStyletron } from '@activebrands/core-web/libs/styletron';
import { getFocusRingStyles } from '@activebrands/core-web/utils/style-functions/get-focus-styles';
import { Dialog } from '@base-ui/react/dialog';
import loadable from '@loadable/component';
import PrimaryMenu from 'components/Header/Navigation/PrimaryMenu';
import ExtraLinks from 'components/Header/Navigation/PrimaryMenu/ExtraLinks';
import FeaturedItems from 'components/Header/Navigation/PrimaryMenu/FeaturedItems';
import PrimaryMenuBackdrop from 'components/Header/Navigation/PrimaryMenu/PrimaryMenuBackdrop';
import SecondaryMenu from 'components/Header/Navigation/SecondaryMenu';
import SiteSelector from 'components/Header/Navigation/SiteSelector';
import PrimaryMenuHeading from 'components/Header/PrimaryMenuHeading';
import StateIcon from 'components/icons/StateIcon';
import Paragraph from 'components/text/Paragraph';

const SiteSelectorOverlay = loadable(() => import('components/overlays/SiteSelectorOverlay'));

const Wrapper = styled('div', ({ $isOpen }) => ({
    height: `calc(var(--vh) - var(--height-header))`,
    left: 0,
    position: 'fixed',
    top: 'var(--height-header)',
    transform: $isOpen ? 'translate3d(0, 0, 0)' : 'translate3d(0, -100%, 0)',
    width: 'calc(100vw - 8px)',
    zIndex: 'var(--zindex-navigation)',
    transitionDuration: $isOpen ? 'var(--duration-medium)' : '0s',
    transitionDelay: $isOpen ? '0s' : 'var(--duration-medium)',
    transitionProperty: 'transform',
    transitionTimingFunction: 'var(--ease)',

    [media.min['tablet.sm']]: {
        width: '100%',
        transform: $isOpen ? 'translate3d(0%, 0, 0)' : 'translate3d(-100%, 0, 0)',
    },
}));

const SiteSelectorWrapper = styled('div', {
    position: 'absolute',
    left: 0,
    bottom: '8px',
    width: '100%',
    height: '57px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    zIndex: 1,
    backgroundColor: 'var(--color-bg-secondary)',
    borderRadius: '16px',

    [media.max['mobile.sm']]: {
        position: 'static',
        width: 'auto',
        margin: '0 8px 8px',
        flexShrink: 0,
    },
});

const InnerWrapper = styled('div', {
    height: '100%',
    position: 'relative',
    maxWidth: 'var(--width-overlays)',
    zIndex: 'calc(var(--zindex-navigation) + 2)',
});

const Section = styled('section', ({ $isVisible }) => ({
    transitionDuration: 'var(--duration-medium)',
    transitionProperty: 'transform',
    transitionDelay: '0s',
    transitionTimingFunction: 'var(--ease)',
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    position: 'absolute',
    transform: $isVisible ? 'translate3d(0, 0, 0)' : 'translate3d(0, -100%, 0)',
    width: '100%',
    zIndex: 'calc(var(--zindex-navigation) + 3)',
    margin: '4px',
    borderRadius: '8px',
    willChange: 'transform',

    '::after': {
        position: 'absolute',
        right: 0,
        bottom: 0,
        display: 'block',
        content: '',
        width: '100%',
        height: '100%',
        zIndex: '-1',
    },

    [media.min['desktop.sm']]: {
        top: 0,
        transform: $isVisible ? 'translate3d(0%, 0, 0)' : 'translate3d(-100%, 0, 0)',
    },
}));

const Nav = styled('nav', ({ $isSiteSelectorOrSubMenuOpen }) => ({
    backgroundColor: 'var(--color-bg-header)',
    color: 'var(--color-text-navigation)',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-start',
    height: `calc(var(--vh) - var(--height-header) - 8px)`, // 8px is the margin
    overflowY: $isSiteSelectorOrSubMenuOpen ? 'hidden' : 'auto',
    overscrollBehavior: 'contain',
    width: '100%',
    zIndex: 'calc(var(--zindex-navigation) + 4)',
    cursor: 'default',
    borderRadius: '16px',

    [media.min['desktop.sm']]: {
        borderRadius: '8px',
    },
}));

const BottomWrapper = styled('div', {
    display: 'flex',
    justifyContent: 'space-between',
    flexDirection: 'row',
    alignItems: 'center',
    width: '100%',
    height: '100%',
    padding: '0 8px',
});

const SelectButton = styled('button', {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    height: '40px',
    width: '100%',
    padding: '0 14px 0 8px',
    border: '1px solid var(--color-bg-button-default)',
    backgroundColor: 'var(--color-bg-button-default)',
    color: 'var(--color-text)',
    borderRadius: '78px',
    ...getFocusRingStyles(),
});

const Navigation = ({
    alternateHrefs = [],
    isOpen = false,
    menuValue = '',
    pageData = {},
    resetNavigation = () => null,
    setMenuValue = () => null,
}) => {
    const [css] = useStyletron();
    const { siteSelectorLinks } = useSiteSelectorQuery();
    const aliasId = useSelector(state => state.application.aliasId);
    const currentSite = siteSelectorLinks.find(site => site.id == aliasId);
    const { lockScroll, unlockScroll } = useScrollLock();
    const [isVisible, setIsVisible] = useState(false);
    const [isSiteSelectorOpen, setIsSiteSelectorOpen] = useState(false);
    const { featuredLabel = '', featuredItems = [], primaryMenu = [], secondaryMenu = [], extraLinks = [] } = pageData;

    useDidUpdate(() => {
        isOpen ? lockScroll() : unlockScroll();

        if (!isOpen) {
            resetNavigation();
            requestAnimationFrame(() => {
                setIsVisible(false);
            });

            if (isSiteSelectorOpen) {
                setIsSiteSelectorOpen(!isSiteSelectorOpen);
            }
        } else {
            requestAnimationFrame(() => {
                setIsVisible(true);
            });
        }
    }, [isOpen]);

    const toggleSiteSelector = () => {
        setIsSiteSelectorOpen(!isSiteSelectorOpen);
        setMenuValue({ current: 0, prev: menuValue.current });
    };

    const isSiteSelectorOrSubMenuOpen = isSiteSelectorOpen || !!menuValue?.current;

    const handleOpenChange = (open, eventDetails) => {
        if (!open && eventDetails?.reason === 'escape-key') {
            resetNavigation();
        }
    };

    return (
        <Dialog.Root modal="trap-focus" open={isOpen} onOpenChange={handleOpenChange}>
            <Dialog.Portal keepMounted renderGuards={false}>
                <Dialog.Popup
                    aria-describedby={undefined}
                    aria-labelledby="navigation-title"
                    aria-modal="true"
                    finalFocus={closeType => closeType === 'keyboard'}
                    render={<Wrapper $isOpen={isOpen} />}
                >
                    <PrimaryMenuBackdrop
                        $isOpen={isOpen}
                        $zIndex="calc(var(--zindex-navigation) + 2)"
                        onClick={() => resetNavigation()}
                    />
                    <InnerWrapper>
                        <Section $isVisible={isVisible}>
                            <Nav
                                $isSiteSelectorOrSubMenuOpen={isSiteSelectorOrSubMenuOpen}
                                $style={{
                                    paddingBottom: isSiteSelectorOrSubMenuOpen ? '0' : '80px',
                                    [media.max['mobile.sm']]: {
                                        paddingBottom: isSiteSelectorOrSubMenuOpen ? '0' : '8px',
                                    },
                                }}
                                data-selector="global.nav.menu"
                                id="navigation"
                                aria-label={fm('Main menu')}
                            >
                                <PrimaryMenuHeading text={fm('Menu')} titleSelector="global.nav.menu.title" />
                                <div
                                    className={css({
                                        display: 'flex',
                                        flexDirection: 'column',
                                        justifyContent: 'space-between',
                                        height: ['auto', null, null, null, '100%'],
                                    })}
                                >
                                    <>
                                        <div
                                            className={css({ padding: '0 8px 24px 8px', zIndex: '1' })}
                                            inert={isSiteSelectorOpen ? '' : undefined}
                                        >
                                            <FeaturedItems
                                                $style={{ marginBottom: '24px' }}
                                                items={featuredItems}
                                                selector="global.nav.menu.featured"
                                                label={featuredLabel}
                                            />
                                            <PrimaryMenu
                                                items={primaryMenu}
                                                menuValue={menuValue}
                                                resetNavigation={resetNavigation}
                                                setMenuValue={setMenuValue}
                                            />
                                            <SecondaryMenu
                                                items={secondaryMenu}
                                                menuValue={menuValue}
                                                setMenuValue={setMenuValue}
                                            />
                                            <ExtraLinks items={extraLinks} />
                                        </div>
                                    </>
                                    <AnimatedOverlay
                                        lockScroll
                                        $style={{ overflow: 'hidden', height: '100%', width: '100%', top: '0' }}
                                        alternateHrefs={alternateHrefs}
                                        animation="slideLTR"
                                        component={SiteSelectorOverlay}
                                        id="site-selector-nav"
                                    />
                                    <SiteSelector
                                        alternateHrefs={alternateHrefs}
                                        id="site-selector-panel"
                                        isSiteSelectorOpen={isSiteSelectorOpen}
                                        setIsSiteSelectorOpen={setIsSiteSelectorOpen}
                                    />
                                    <SiteSelectorWrapper
                                        $style={{ display: isSiteSelectorOrSubMenuOpen ? 'none' : 'block' }}
                                    >
                                        <BottomWrapper>
                                            <SelectButton
                                                type="button"
                                                aria-expanded={isSiteSelectorOpen}
                                                data-selector="global.nav.menu.country"
                                                aria-controls="site-selector-panel"
                                                onClick={() => toggleSiteSelector()}
                                            >
                                                <div
                                                    className={css({
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'space-between',
                                                    })}
                                                >
                                                    <div
                                                        className={css({
                                                            marginRight: '16px',
                                                            borderRadius: '60px',
                                                            width: '24px',
                                                            height: '24px',
                                                            overflow: 'hidden',
                                                        })}
                                                    >
                                                        <Image
                                                            alt="" // Empty alt is intentional since the flag is decorative
                                                            sizes={['24px']}
                                                            src={{ url: currentSite?.icon, width: [24, 48, 72] }}
                                                        />
                                                    </div>
                                                    <Paragraph fontKeys="Miscellaneous/16_100_-1">
                                                        {currentSite?.label}
                                                    </Paragraph>
                                                </div>
                                                <StateIcon
                                                    state={isSiteSelectorOpen ? 'close' : 'open'}
                                                    width="24px"
                                                    onClick={() => overlay.close(OVERLAY_ID.SITE_SELECTOR)}
                                                />
                                            </SelectButton>
                                        </BottomWrapper>
                                    </SiteSelectorWrapper>
                                </div>
                            </Nav>
                        </Section>
                    </InnerWrapper>
                </Dialog.Popup>
            </Dialog.Portal>
        </Dialog.Root>
    );
};

Navigation.propTypes = {
    alternateHrefs: PropTypes.array,
    isOpen: PropTypes.bool,
    menuValue: PropTypes.object,
    pageData: PropTypes.object,
    resetNavigation: PropTypes.func,
    setMenuValue: PropTypes.func,
};

export default Navigation;
